import { CreateLoanDto, LoanInfosDto, LoanTierDto, UpdateLoanDto } from 'api/borrower';
import { ReferentialKey } from 'api/referential';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { useBorrowerProjectId } from 'features/subscription-process/hooks/useBorrowerProjectId';
import { resetPendingForm } from 'features/subscription-process/slices/subscriptionProcessSlice';
import { FormikProps } from 'formik';
import { usePanel } from 'hooks/usePanel';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { borrowersAPI } from 'services/swagger-api';
import { getUpdatedFields } from 'shared/utils/getUpdatedFields';
import {
  areTierRatesUniform,
  borrowerLoanFormConfig,
  BorrowerLoanFormFieldNames,
  BorrowerLoanFormValues,
  createDefaultTiers,
  LoanTypeCodes
} from './borrowerLoanForm';

interface UseBorrowerLoanFormActionsProps {
  currentLoan?: LoanInfosDto;
  onSuccess: () => void;
  /**
   * Id du projet courant (figé depuis l'URL à l'ouverture du panneau). Doit être utilisé
   * en priorité : le `riskId` Redux peut être périmé après un changement de simulation et
   * rattacherait le prêt au MAUVAIS projet.
   */
  borrowerProjectId?: string;
}

/**
 * Normalise les paliers avant envoi au back. Règle métier :
 * - "taux par palier" (tieredRate ON) : chaque palier garde son taux ; le taux
 *   global du prêt est repris depuis le 1er palier.
 * - "taux global" (tieredRate OFF) : le taux global saisi est reporté sur chaque
 *   palier.
 * Un palier envoyé porte donc toujours ses 3 infos (durée, montant, taux).
 * Pour un prêt non-palier, on renvoie un tableau vide (purge d'éventuels paliers
 * résiduels côté back) sans toucher au taux global.
 */
const normalizeTiersForSubmit = (
  values: BorrowerLoanFormValues
): { tiers: LoanTierDto[]; interestRatePercentage: number } => {
  if (values.loanType !== LoanTypeCodes.Palier) {
    return { tiers: [], interestRatePercentage: values.interestRatePercentage };
  }

  const rawTiers = values.tiers ?? [];
  const tieredRate = values.tieredRate === true;

  const globalRate = tieredRate
    ? Number(rawTiers[0]?.rate ?? 0)
    : Number(values.interestRatePercentage ?? 0);

  const tiers: LoanTierDto[] = rawTiers.map((tier) => ({
    duration: tier.duration ?? null,
    amount: tier.amount ?? null,
    rate: tieredRate ? (tier.rate ?? null) : globalRate
  }));

  return { tiers, interestRatePercentage: globalRate };
};

export const useBorrowerLoanFormActions = ({
  currentLoan,
  onSuccess,
  borrowerProjectId: borrowerProjectIdProp
}: UseBorrowerLoanFormActionsProps) => {
  // L'URL fait foi : id passé en prop, sinon param de route ; le store Redux n'est
  // qu'un dernier repli (évite un id périmé, ex. depuis le comparateur emprunteur).
  const projectIdFromUrlOrStore = useBorrowerProjectId();
  const borrowerProjectId = borrowerProjectIdProp ?? projectIdFromUrlOrStore;
  const dispatch = useAppDispatch();

  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loanTypeReferential = useAppSelector(
    selectReferentialDataByKey(ReferentialKey.LoanType)
  );
  const interestRateTypeReferential = useAppSelector(
    selectReferentialDataByKey(ReferentialKey.InterestRateType)
  );
  const defermentTypeReferential = useAppSelector(
    selectReferentialDataByKey(ReferentialKey.DefermentType)
  );

  const { setPanelActions, closePanel } = usePanel();
  const formikRef = useRef<FormikProps<BorrowerLoanFormValues>>(null);

  // Mapper les données du loan vers les valeurs du formulaire
  const initialValues = useMemo((): BorrowerLoanFormValues => {
    if (!currentLoan) {
      return borrowerLoanFormConfig.initialValues;
    }

    const interestRateType = interestRateTypeReferential.find(
      (item) => item.id === currentLoan.interestRateTypeId
    );
    const loanType = loanTypeReferential.find(
      (item) => item.id === currentLoan.loanTypeId
    );
    const defermentType = defermentTypeReferential.find(
      (item) => item.id === currentLoan.defermentTypeId
    );

    // Inférence du toggle "taux par palier" : si tous les paliers partagent le
    // même taux, c'est qu'un taux global a été reporté → toggle OFF. Sinon, des
    // taux distincts ont été saisis → toggle ON. (Source unique : areTierRatesUniform.)
    const tieredRate =
      (currentLoan.tiers?.length ?? 0) > 0 &&
      !areTierRatesUniform(currentLoan.tiers);

    return {
      ...currentLoan,
      [BorrowerLoanFormFieldNames.LoanType]: loanType ? loanType.code : '',
      [BorrowerLoanFormFieldNames.InterestRateType]: interestRateType
        ? interestRateType.code
        : '',
      [BorrowerLoanFormFieldNames.DefermentType]: defermentType
        ? defermentType.code
        : '',
      [BorrowerLoanFormFieldNames.TieredRate]: tieredRate,
      [BorrowerLoanFormFieldNames.Tiers]: currentLoan.tiers?.length
        ? currentLoan.tiers
        : createDefaultTiers()
    };
  }, [
    currentLoan,
    defermentTypeReferential,
    interestRateTypeReferential,
    loanTypeReferential
  ]);

  const deleteLoan = useCallback(async (loanId: string) => {
    setIsLoading(true);
    try {
      await borrowersAPI.deleteLoan({ loanId });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateLoan = useCallback(
    async (values: BorrowerLoanFormValues) => {
      const { id: loanId, ...rest } = values;
      if (!loanId) return;

      setIsLoading(true);
      try {
        const newValues = getUpdatedFields(initialValues, rest);
        const interestRateType = interestRateTypeReferential.find(
          (item) => item.code === newValues.interestRateType
        );
        const loanType = loanTypeReferential.find(
          (item) => item.code === newValues.loanType
        );
        const defermentType = defermentTypeReferential.find(
          (item) => item.code === newValues.defermentType
        );
        const { tiers, interestRatePercentage } =
          normalizeTiersForSubmit(values);
        const updateLoanDto: UpdateLoanDto = {
          ...newValues,
          loanTypeId: loanType ? loanType.id : undefined,
          interestRateTypeId: interestRateType
            ? interestRateType.id
            : undefined,
          defermentTypeId: defermentType ? defermentType.id : undefined,
          // Remplacement complet des paliers (tableau vide = purge pour un prêt
          // non-palier). Le taux global n'est repris du 1er palier que pour un
          // prêt à paliers.
          tiers,
          ...(values.loanType === LoanTypeCodes.Palier
            ? { interestRatePercentage }
            : {})
        };
        // Note : UpdateLoanDto ne possède pas remainingDuration/residualValue
        // dans son schéma OpenAPI — ces champs ne sont donc pas envoyés en
        // update même si la valeur formik les contient (ils sont strippés
        // par UpdateLoanDtoToJSON). Rien à nettoyer ici.
        await borrowersAPI.updateLoan({ loanId, updateLoanDto });
        onSuccess();
      } finally {
        setIsLoading(false);
      }
    },
    [
      initialValues,
      defermentTypeReferential,
      loanTypeReferential,
      interestRateTypeReferential,
      onSuccess
    ]
  );

  const createLoan = useCallback(
    async (values: BorrowerLoanFormValues) => {
      if (!borrowerProjectId) return;

      const interestRateType = interestRateTypeReferential.find(
        (item) => item.code === values.interestRateType
      );
      const loanType = loanTypeReferential.find(
        (item) => item.code === values.loanType
      );
      const defermentType = defermentTypeReferential.find(
        (item) => item.code === values.defermentType
      );

      setIsLoading(true);
      try {
        const { tiers, interestRatePercentage } =
          normalizeTiersForSubmit(values);
        const createLoanDto: CreateLoanDto = {
          ...values,
          borrowerProjectId,
          loanTypeId: loanType ? loanType.id : '',
          interestRateTypeId: interestRateType ? interestRateType.id : '',
          defermentTypeId: defermentType ? defermentType.id : '',
          tiers,
          interestRatePercentage
        };
        // Crédit-bail : durée restante / valeur résiduelle ne sont plus
        // saisies côté front, on s'assure qu'aucune valeur héritée ne fuite.
        if (values.loanType === LoanTypeCodes.CreditBail) {
          createLoanDto.remainingDuration = undefined;
          createLoanDto.residualValue = undefined;
        }
        await borrowersAPI.createLoan({ createLoanDto });
        onSuccess();
      } finally {
        setIsLoading(false);
      }
    },
    [
      loanTypeReferential,
      interestRateTypeReferential,
      defermentTypeReferential,
      borrowerProjectId,
      onSuccess
    ]
  );

  const handleSubmit = useCallback(
    async (values: BorrowerLoanFormValues) => {
      if (currentLoan?.id) {
        await updateLoan(values);
      } else {
        await createLoan(values);
      }
      closePanel();
    },
    [closePanel, currentLoan?.id, updateLoan, createLoan]
  );

  const handleValidationChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  // Configuration des actions du Panel
  useEffect(() => {
    const basePanelActions = {
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      submitDisabled: !isFormValid || isLoading,
      onSubmit: () => {
        formikRef.current?.submitForm();
        dispatch(resetPendingForm());
      },
      onClose: () => {
        dispatch(resetPendingForm());
      }
    };

    if (currentLoan) {
      setPanelActions({
        ...basePanelActions,
        onDelete: async () => {
          if (currentLoan.id) {
            await deleteLoan(currentLoan.id);
            dispatch(resetPendingForm());
            closePanel();
            onSuccess();
          }
        },
        deleteLabel: 'COMMON.DELETE',
        submitDisabled: !isFormValid || isLoading
      });
      return;
    }

    if (formikRef.current) {
      setPanelActions(basePanelActions);
    }
  }, [
    dispatch,
    currentLoan,
    formikRef,
    isFormValid,
    isLoading,
    setPanelActions,
    deleteLoan,
    onSuccess,
    closePanel
  ]);

  return {
    formikRef,
    initialValues,
    handleSubmit,
    handleValidationChange,
    isLoading
  };
};
